import * as React from 'react'
import { AlertTriangle, Check } from 'lucide-react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatCurrency } from '@/lib/currency-utils'
import { cn } from '@/lib/utils'
import type { QuoteBuilderChecklistItem, QuoteBuilderSectionId } from './quote-builder-readiness'

export type QuoteBuilderRailScheduleRow = {
  key: string
  label: string
  amountCents: number
}

export interface QuoteBuilderSummaryRailProps {
  totalCents: number
  subtotalCents: number
  discountAmountCents: number
  discountLabel?: string
  taxAmountCents: number
  taxLabel?: string
  tipCents: number
  tipLabel?: string
  schedules: QuoteBuilderRailScheduleRow[]
  checklist: QuoteBuilderChecklistItem[]
  onChecklistSelect: (section: QuoteBuilderSectionId) => void
  actions: React.ReactNode
  preview: React.ReactNode
  totalLabel?: string
  checklistHeading?: string
  footerHint?: string
}

/**
 * Sticky right rail of the quote builder: a real tab control switching between
 * the running Summary (totals, "Before you send" checklist, actions) and a
 * live customer Preview.
 */
export function QuoteBuilderSummaryRail({
  totalCents,
  subtotalCents,
  discountAmountCents,
  discountLabel,
  taxAmountCents,
  taxLabel,
  tipCents,
  tipLabel,
  schedules,
  checklist,
  onChecklistSelect,
  actions,
  preview,
  totalLabel = 'Quote total',
  checklistHeading = 'Before you send',
  footerHint = 'Flip to Preview to see exactly what the client receives.',
}: QuoteBuilderSummaryRailProps) {
  return (
    <Tabs defaultValue="summary" className="gap-4">
      <TabsList className="grid w-full grid-cols-2 rounded-xl bg-muted p-1">
        <TabsTrigger value="summary" className="rounded-lg">Summary</TabsTrigger>
        <TabsTrigger value="preview" className="rounded-lg">Preview</TabsTrigger>
      </TabsList>

      <TabsContent value="summary" className="space-y-4">
        <div className="rounded-2xl border bg-card p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{totalLabel}</p>
          <p className="mt-1 text-4xl font-semibold tabular-nums">${formatCurrency(totalCents)}</p>

          <div className="mt-4 space-y-2 border-t pt-4 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="tabular-nums">${formatCurrency(subtotalCents)}</span>
            </div>
            {discountAmountCents > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{discountLabel || 'Discount'}</span>
                <span className="tabular-nums text-status-success-fg">
                  −${formatCurrency(discountAmountCents)}
                </span>
              </div>
            )}
            {taxAmountCents > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{taxLabel || 'Sales tax'}</span>
                <span className="tabular-nums">${formatCurrency(taxAmountCents)}</span>
              </div>
            )}
            {tipCents > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{tipLabel || 'Enforced tip'}</span>
                <span className="tabular-nums">${formatCurrency(tipCents)}</span>
              </div>
            )}
          </div>

          {schedules.length > 0 && (
            <div className="mt-4 space-y-2 border-t pt-4 text-sm">
              {schedules.map((schedule) => (
                <div key={schedule.key} className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{schedule.label}</span>
                  <span className="tabular-nums">${formatCurrency(schedule.amountCents)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border bg-card p-5">
          <p className="text-sm font-medium">{checklistHeading}</p>
          <ul className="mt-3 space-y-2">
            {checklist.map((item) => {
              const rowContent = (
                <>
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex size-6 shrink-0 items-center justify-center rounded-full',
                      item.satisfied
                        ? 'bg-status-success-bg text-status-success-fg'
                        : 'bg-destructive/10 text-destructive',
                    )}
                  >
                    {item.satisfied
                      ? <Check className="size-3.5" />
                      : <AlertTriangle className="size-3.5" />}
                  </span>
                  <span className={cn('min-w-0 text-left', !item.satisfied && 'text-destructive')}>
                    {item.label}
                  </span>
                </>
              )

              return (
                <li key={item.id}>
                  {item.satisfied || !item.section ? (
                    <span className="flex items-center gap-3 text-sm">
                      <span className="sr-only">
                        {item.satisfied ? 'Ready: ' : 'Needs attention: '}
                      </span>
                      {rowContent}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onChecklistSelect(item.section as QuoteBuilderSectionId)}
                      className="flex w-full items-center gap-3 rounded-lg text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                    >
                      <span className="sr-only">Needs attention: </span>
                      {rowContent}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>

        <div className="rounded-2xl border bg-card p-5">
          <div className="flex flex-col gap-2">{actions}</div>
        </div>

        <p className="text-xs text-muted-foreground">
          {footerHint}
        </p>
      </TabsContent>

      <TabsContent value="preview">{preview}</TabsContent>
    </Tabs>
  )
}
